export const {
  VITE_APP_ENV: appEnv,
  VITE_API_URL: apiUrl,
  VITE_SEARCH_API_URL: searchApiUrl,
  VITE_SERVICE_API_URL: serviceApiUrl,
  VITE_GRAPHQL_WS: graphWs,
  VITE_OIDC_AUTHORITY: oidcAuthority,
  VITE_OIDC_CLIENT_ID: oidcClientId,
  VITE_OIDC_REDIRECT_URI: oidcRedirectUri,
  VITE_MAPBOX_TOKEN: mapboxToken,
  VITE_HEAP_ENV_ID: heapEnvId,
  VITE_FEATUREHUB_API_KEY: featureHubApiKey,
  VITE_STRIPE_TABLE_ID: stripeTableId,
  VITE_STRIPE_PUBLIC_KEY: stripePublicKey,
  VITE_STRIPE_BILLING_URL: stripeBillingUrl,
}: ImportMetaEnv = import.meta.env

export const isLocal =
  appEnv === 'local' || window.location.hostname === 'localhost'
export const isDevelopment = appEnv === 'dev'
export const isStaging = appEnv === 'stage'
export const isDemonstration = appEnv === 'demo'
export const isProduction = appEnv === 'prod'

export const isGov = appEnv === 'gov'

export const REDIRECT_COOKIE_NAME = 'RedirectAfterLogin'
export const REDIRECT_PATH = '/redirect'

export const ROUTES = {
  account: '/account',
  password: '/account/password',
  users: '/account/users',
  help: '/help',
  billing: '/account/billing',
  planSelection: '/plan-selection',
  login: '/log-in',
  accessDenied: '/access-denied',
  error: '/error',
  root: '/',
  results: '/results',
  resultsItem: '/results/:itemId',
  overview: '/overview',
  news: '/news',
  images: '/images',
  fires: '/fires',
  ships: '/ships',
  social: '/social',
  acceptShare: '/accept-share',
  acceptShareToken: '/accept-share/:collectionId/:shareToken',
  collections: '/collections',
  collectionDetail: '/collections/:collectionId',
  collectionItemDetail: '/collections/:collectionId/items/:itemId',
  history: '/history',
  map: '/map',
  mapItem: '/map/:itemId',
  address: '/address',
  addressPath: '/address/:mock?',
  terrapin: '/terrapin',
  terrapinItem: '/terrapin/:terrapinItemId',
  admin: '/admin',
  allProperty: '/properties',
  timeline: '/timeline',
  notFound: '*',
}

export const DATA_CATEGORIES = {
  IMAGE: 'IMAGE',
  DATA: 'DATA',
  PUBLICATION: 'WEB_ARTICLE',
  SOCIAL_MEDIA: 'SOCIAL_MEDIA',
  INSIGHT: 'INSIGHT',
  RELATED_IMAGERY: 'RELATED_IMAGERY',
  ANALYTIC: 'ANALYTIC',
  PROPERTY: 'PROPERTY',
  WEB_ARTICLE: 'WEB_ARTICLE',
  FILE: 'FILE',
} as const

export const DATA_SOURCES = {
  BLACKSKY: 'BLACKSKY',
  VEXCEL: 'VEXCEL',
  UP42: 'UP42',
} as const

export const RESULT_CATEGORIES = {
  INSIGHT: 'INSIGHT',

  ATTOM: 'ATTOM',
  FIRE: 'FIRE',
  HAZARD_PROPERTY: 'HAZARD_PROPERTY',
  HAZARD_RISK: 'HAZARD_RISK',
  IMAGE: 'IMAGE',
  PUBLICATION: 'WEB_ARTICLE',
  REGRID: 'REGRID',
  SHIP: 'SHIP',
  SOCIAL_MEDIA: 'SOCIAL_MEDIA',

  RELATED_IMAGERY: 'RELATED_IMAGERY',
  ANALYTIC: 'ANALYTIC',
  PROPERTY: 'PROPERTY',
  WEB_ARTICLE: 'WEB_ARTICLE',
  VESSEL: 'VESSEL',
  PORT: 'PORT',
  FILE: 'FILE',
  DATA: 'DATA',
} as const

export const EMPTY_RESULT_IDS = {
  IMAGE: [] as string[],
  DATA: [] as string[],
  WEB_ARTICLE: [] as string[],
  SOCIAL_MEDIA: [] as string[],
  INSIGHT: [] as string[],
  RELATED_IMAGERY: [] as string[],
  PROPERTY: [] as string[],
} as const

export const EMPTY_CATEGORIZED_RESULTS = {
  imageResults: [],
  dataResults: [],
  publicationResults: [],
  socialMediaResults: [],
  insightResults: [],
  propertyResults: [],
} as const

export const SCORE_COLORS = {
  F: '#C62828',
  D: '#ED6C02',
  C: '#DFA702',
  B: '#829A24',
  A: '#2E7D32',
} as { [key: string]: string }

export const SOCIAL_COLORS = {
  facebook: '#0091EA',
  youtube: '#FF0000',
  instagram: '#E1306C',
  reddit: '#FF4500',
  vk: '#4C75A3',
  weibo: '#E6162D',
  toutiao: '#FF8247',
  telegram: '#0088cc',
} as { [key: string]: string }

export const RESULT_CATEGORY_COLORS = {
  attom: '#834DD3',
  fire: '#D32F2f',
  ship: '#00A5B0',
  news: '#ffa726',
  social: '#4CAF50',
}

export const ICONS = {
  construction: 'construction',
  localFireDepartment: 'local_fire_department',
  satellite: 'satellite',
  newspaper: 'newspaper',
  anchor: 'anchor',
  satelliteAlt: 'satellite_alt',
  add: 'add',
  openInNew: 'open_in_new',
  download: 'download',
  map: 'map',
  chartData: 'chart_data',
  iosShare: 'ios_share',
  info: 'info',
}

// Result menu options
export const MENU_OPTION_KEYS = {
  addToCollection: 'addToCollection',
  visitArticle: 'visitArticle',
  viewDetails: 'viewDetails',
  downloadImage: 'downloadImage',
  viewOnMap: 'viewOnMap',
  sendToAnalytics: 'sendToAnalytics',
  share: 'share',
} as const

export const MENU_OPTION_LABELS = {
  [MENU_OPTION_KEYS.addToCollection]: 'Add to Collection',
  [MENU_OPTION_KEYS.visitArticle]: 'Visit Article',
  [MENU_OPTION_KEYS.downloadImage]: 'Download',
  [MENU_OPTION_KEYS.viewOnMap]: 'View on Map',
  [MENU_OPTION_KEYS.sendToAnalytics]: 'Analyze',
  [MENU_OPTION_KEYS.viewDetails]: 'View Details',
  [MENU_OPTION_KEYS.share]: 'Share',
} as const

export const MENU_OPTION_ICONS = {
  [MENU_OPTION_KEYS.addToCollection]: ICONS.add,
  [MENU_OPTION_KEYS.visitArticle]: ICONS.openInNew,
  [MENU_OPTION_KEYS.downloadImage]: ICONS.download,
  [MENU_OPTION_KEYS.viewOnMap]: ICONS.map,
  [MENU_OPTION_KEYS.sendToAnalytics]: ICONS.chartData,
  [MENU_OPTION_KEYS.share]: ICONS.iosShare,
  [MENU_OPTION_KEYS.viewDetails]: ICONS.info,
} as const

export const FUTURE_MENU_OPTIONS = [
  MENU_OPTION_KEYS.sendToAnalytics,
  MENU_OPTION_KEYS.share,
] as const

export const FUTURE_OPTION_TITLE = 'Coming Soon'

export const DEFAULT_ACTIONS_TO_HIDE = [
  MENU_OPTION_KEYS.addToCollection,
  MENU_OPTION_KEYS.visitArticle,
  MENU_OPTION_KEYS.downloadImage,
  MENU_OPTION_KEYS.share,
  MENU_OPTION_KEYS.sendToAnalytics,
]

export const COLLECTIBLE_ACTIONS_TO_HIDE = [
  MENU_OPTION_KEYS.visitArticle,
  MENU_OPTION_KEYS.downloadImage,
  MENU_OPTION_KEYS.share,
  MENU_OPTION_KEYS.sendToAnalytics,
]
export const MAP_BASE_LAYERS = {
  light: 'mapbox/light-v11',
  dark: 'mapbox/dark-v11',
  satellite: 'mapbox/satellite-streets-v9',
  streets: 'mapbox/streets-v11',
  dantiStreets: 'danti-ai/clygagdfr00w601r4bu5nd8l3',
} as const

export const FEATURE_FLAG_LOCAL_KEYS = {
  timeline: 'timeline',
  insightFeedback: 'insightFeedback',
  fauxAnalytic: 'fauxAnalytic',
} as const
export type FeatureFlagLocalKeys = keyof typeof FEATURE_FLAG_LOCAL_KEYS

export const FEATURE_FLAG_LOCAL_VALUES = {
  [FEATURE_FLAG_LOCAL_KEYS.timeline]: 'timeline',
  [FEATURE_FLAG_LOCAL_KEYS.fauxAnalytic]: 'fauxAnalytic',
  [FEATURE_FLAG_LOCAL_KEYS.insightFeedback]: 'insightFeedback',
} as Record<FeatureFlagLocalKeys, string>

type ValueOf<T> = T[keyof T]
export type FeatureFlagLocalValues = ValueOf<typeof FEATURE_FLAG_LOCAL_VALUES>

export const FEATURE_FLAG_ENV_VARIABLES = {
  [FEATURE_FLAG_LOCAL_VALUES.timeline]: 'VITE_TIMELINE',
  [FEATURE_FLAG_LOCAL_VALUES.fauxAnalytic]: 'VITE_FAUX_ANALYTIC',
  [FEATURE_FLAG_LOCAL_VALUES.insightFeedback]: 'VITE_INSIGHT_FEEDBACK',
} as Record<FeatureFlagLocalValues, string>
